/**
 * 管理员角色管理相关 Store
 * 负责角色数据的加载、管理和操作
 */

import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useCacheStore } from '../cache'
import { adminRolesApi } from '@/https'
import type { PaginationData } from '@/https/request'
import type {
    Role,
    RoleListRequest,
    RoleCreateRequest,
    RoleUpdateRequest,
    RolePermissionAssignRequest,
    RolePermissionRevokeRequest,
    RolePermissionAssignResponse,
    RolePermissionRevokeResponse,
    UserRoleAssignRequest,
    UserRoleRevokeRequest,
    UserRoleAssignResponse,
    UserRoleRevokeResponse,
    UserRoleListRequest,
    UserRoleListItem,
    RolePermissionsResponse,
    RoleUsersResponse
} from '@/https/types_admin/roles'
import type { Permission } from '@/https/types_admin/permissions'

export const useAdminRolesStore = defineStore('admin-roles', () => {
    const cacheStore = useCacheStore()

    // ===== 状态管理 =====
    const roleList = ref<Role[]>([])
    const currentRoleDetail = ref<Role | null>(null)
    const currentRoleUsers = ref<Array<{
        id: number
        username: string
        phone: string
        granted_at: string
        expires_at?: string | null
        is_active: boolean
    }>>([])
    const currentRolePermissions = ref<Permission[]>([])
    const userRoleList = ref<UserRoleListItem[]>([])

    // 分页信息
    const rolePagination = ref({
        total: 0,
        page: 1,
        size: 20,
        has_more: false
    })

    const userRolePagination = ref({
        total: 0,
        page: 1,
        size: 20,
        has_more: false
    })

    // 查询参数
    const lastRoleQuery = ref<RoleListRequest>({})
    const lastUserRoleQuery = ref<UserRoleListRequest>({})

    // 加载状态
    const roleListLoading = ref(false)
    const roleDetailLoading = ref(false)
    const roleOperationLoading = ref(false)
    const roleUsersLoading = ref(false)
    const permissionOperationLoading = ref(false)
    const userRoleListLoading = ref(false)
    const userRoleOperationLoading = ref(false)

    // 错误状态
    const roleListError = ref<string | null>(null)
    const roleDetailError = ref<string | null>(null)
    const roleOperationError = ref<string | null>(null)
    const permissionOperationError = ref<string | null>(null)
    const userRoleListError = ref<string | null>(null)
    const userRoleOperationError = ref<string | null>(null)

    // ===== 计算属性 =====

    // 系统角色
    const systemRoles = computed(() =>
        roleList.value.filter(role => role.is_system)
    )

    // 自定义角色
    const customRoles = computed(() =>
        roleList.value.filter(role => !role.is_system)
    )

    // 活跃角色
    const activeRoles = computed(() =>
        roleList.value.filter(role => role.is_active)
    )

    // 禁用角色
    const inactiveRoles = computed(() =>
        roleList.value.filter(role => !role.is_active)
    )

    // 角色统计
    const roleStats = computed(() => ({
        total: roleList.value.length,
        system: systemRoles.value.length,
        custom: customRoles.value.length,
        active: activeRoles.value.length,
        inactive: inactiveRoles.value.length
    }))

    // 按权限数量排序的角色（通过当前权限数据计算）
    const rolesByPermissionCount = computed(() => {
        return [...roleList.value].sort((a, b) => {
            // 由于后端角色列表不返回权限数量统计，这里按角色名称排序作为替代
            return a.display_name.localeCompare(b.display_name)
        })
    })

    // 按用户数量排序的角色（通过当前用户数据计算）
    const rolesByUserCount = computed(() => {
        return [...roleList.value].sort((a, b) => {
            // 由于后端角色列表不返回用户数量统计，这里按角色名称排序作为替代
            return a.display_name.localeCompare(b.display_name)
        })
    })

    // ===== 角色列表操作 =====

    /**
     * 加载角色列表
     */
    const loadRoleList = async (params: RoleListRequest = {}, forceRefresh = false) => {
        const cacheKey = `admin-roles-list-${JSON.stringify(params)}`

        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                const data = cached as PaginationData<Role>
                roleList.value = data.items
                rolePagination.value = {
                    total: data.total,
                    page: data.page,
                    size: data.size,
                    has_more: data.has_more
                }
                return data
            }
        }

        roleListLoading.value = true
        roleListError.value = null

        try {
            const response = await adminRolesApi.getRoleList(params)

            roleList.value = response.items
            rolePagination.value = {
                total: response.total,
                page: response.page,
                size: response.size,
                has_more: response.has_more
            }
            lastRoleQuery.value = params

            // 缓存结果
            cacheStore.setCache(cacheKey, response, 5 * 60 * 1000) // 5分钟缓存

            return response
        } catch (error: unknown) {
            if (error instanceof Error) {
                roleListError.value = error.message || '加载角色列表失败'
            } else {
                roleListError.value = '加载角色列表失败'
            }
            throw error
        } finally {
            roleListLoading.value = false
        }
    }


    /**
     * 获取角色用户列表
     */
    const getRoleUsers = async (id: number, forceRefresh = false) => {
        const cacheKey = `admin-role-users-${id}`

        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                currentRoleUsers.value = (cached as RoleUsersResponse).users || []
                return cached
            }
        }

        roleUsersLoading.value = true

        try {
            const response = await adminRolesApi.getRoleUsers(id)
            currentRoleUsers.value = response.users

            // 缓存结果
            cacheStore.setCache(cacheKey, response, 5 * 60 * 1000) // 5分钟缓存

            return response
        } catch (error: unknown) {
            if (error instanceof Error) {
                console.error('获取角色用户失败:', error)
            } else {
                console.error('获取角色用户失败:', error)
            }
            throw error
        } finally {
            roleUsersLoading.value = false
        }
    }

    /**
     * 获取角色权限列表
     */
    const getRolePermissions = async (id: number, forceRefresh = false) => {
        const cacheKey = `admin-role-permissions-${id}`

        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                currentRolePermissions.value = (cached as RolePermissionsResponse).permissions || []
                return cached
            }
        }

        try {
            const response = await adminRolesApi.getRolePermissions(id)
            currentRolePermissions.value = response.permissions

            // 缓存结果
            cacheStore.setCache(cacheKey, response, 10 * 60 * 1000) // 10分钟缓存

            return response
        } catch (error: unknown) {
            if (error instanceof Error) {
                console.error('获取角色权限失败:', error)
            } else {
                console.error('获取角色权限失败:', error)
            }
            throw error
        }
    }

    /**
     * 创建角色
     */
    const createRole = async (data: RoleCreateRequest) => {
        roleOperationLoading.value = true
        roleOperationError.value = null

        try {
            const response = await adminRolesApi.createRole(data)

            // 更新本地列表
            roleList.value.unshift(response)
            rolePagination.value.total += 1

            // 清除相关缓存
            clearRoleCache()

            return response
        } catch (error: unknown) {
            if (error instanceof Error) {
                roleOperationError.value = error.message || '创建角色失败'
            } else {
                roleOperationError.value = '创建角色失败'
            }
            throw error
        } finally {
            roleOperationLoading.value = false
        }
    }

    /**
     * 更新角色
     */
    const updateRole = async (id: number, data: RoleUpdateRequest) => {
        roleOperationLoading.value = true
        roleOperationError.value = null

        try {
            const response = await adminRolesApi.updateRole(id, data)

            // 更新本地列表
            const index = roleList.value.findIndex(r => r.id === id)
            if (index !== -1) {
                roleList.value[index] = response
            }

            // 更新当前详情
            if (currentRoleDetail.value?.id === id) {
                currentRoleDetail.value = response
            }

            // 清除相关缓存
            clearRoleCache()

            return response
        } catch (error: unknown) {
            if (error instanceof Error) {
                roleOperationError.value = error.message || '更新角色失败'
            } else {
                roleOperationError.value = '更新角色失败'
            }
            throw error
        } finally {
            roleOperationLoading.value = false
        }
    }

    /**
     * 删除角色
     */
    const deleteRole = async (id: number) => {
        roleOperationLoading.value = true
        roleOperationError.value = null

        try {
            await adminRolesApi.deleteRole(id)

            // 从本地列表中移除
            const index = roleList.value.findIndex(r => r.id === id)
            if (index !== -1) {
                roleList.value.splice(index, 1)
                rolePagination.value.total -= 1
            }

            // 清除当前详情
            if (currentRoleDetail.value?.id === id) {
                currentRoleDetail.value = null
            }

            // 清除相关缓存
            clearRoleCache()

            return true
        } catch (error: unknown) {
            if (error instanceof Error) {
                roleOperationError.value = error.message || '删除角色失败'
            } else {
                roleOperationError.value = '删除角色失败'
            }
            throw error
        } finally {
            roleOperationLoading.value = false
        }
    }

    // ===== 权限分配操作 =====

    /**
     * 加载用户角色列表
     */
    const loadUserRoleList = async (params: UserRoleListRequest = {}, forceRefresh = false) => {
        const cacheKey = `admin-user-roles-list-${JSON.stringify(params)}`

        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                const data = cached as PaginationData<UserRoleListItem>
                userRoleList.value = data.items
                userRolePagination.value = {
                    total: data.total,
                    page: data.page,
                    size: data.size,
                    has_more: data.has_more
                }
                return data
            }
        }

        userRoleListLoading.value = true
        userRoleListError.value = null

        try {
            const response = await adminRolesApi.getUserRolesList(params)

            userRoleList.value = response.items
            userRolePagination.value = {
                total: response.total,
                page: response.page,
                size: response.size,
                has_more: response.has_more
            }
            lastUserRoleQuery.value = params

            // 缓存结果
            cacheStore.setCache(cacheKey, response, 5 * 60 * 1000) // 5分钟缓存

            return response
        } catch (error: unknown) {
            if (error instanceof Error) {
                userRoleListError.value = error.message || '加载用户角色列表失败'
            } else {
                userRoleListError.value = '加载用户角色列表失败'
            }
            throw error
        } finally {
            userRoleListLoading.value = false
        }
    }

    /**
     * 批量分配用户角色
     */
    const assignUserRoles = async (data: UserRoleAssignRequest): Promise<UserRoleAssignResponse> => {
        userRoleOperationLoading.value = true
        userRoleOperationError.value = null

        try {
            const response = await adminRolesApi.assignUserRoles(data)

            // 如果后端返回部分失败，抛错给调用方以便统一提示
            const respObj = response as unknown as { failed?: unknown[]; success?: unknown[] }
            const failedCount = Array.isArray(respObj.failed) ? respObj.failed.length : 0
            const successCount = Array.isArray(respObj.success) ? respObj.success.length : 0
            if (failedCount > 0) {
                throw new Error(`分配成功 ${successCount} 项，失败 ${failedCount} 项`)
            }

            // 清除相关缓存并刷新当前列表
            clearRoleCache()
            try {
                await loadUserRoleList({ ...(lastUserRoleQuery.value || {}), page: userRolePagination.value.page, page_size: userRolePagination.value.size }, true)
            } catch { }

            return response
        } catch (error: unknown) {
            if (error instanceof Error) {
                userRoleOperationError.value = error.message || '批量分配用户角色失败'
            } else {
                userRoleOperationError.value = '批量分配用户角色失败'
            }
            throw error
        } finally {
            userRoleOperationLoading.value = false
        }
    }

    /**
     * 批量撤销用户角色
     */
    const revokeUserRoles = async (data: UserRoleRevokeRequest): Promise<UserRoleRevokeResponse> => {
        userRoleOperationLoading.value = true
        userRoleOperationError.value = null

        try {
            const response = await adminRolesApi.revokeUserRoles(data)

            // 清除相关缓存
            clearRoleCache()

            return response
        } catch (error: unknown) {
            if (error instanceof Error) {
                userRoleOperationError.value = error.message || '批量撤销用户角色失败'
            } else {
                userRoleOperationError.value = '批量撤销用户角色失败'
            }
            throw error
        } finally {
            userRoleOperationLoading.value = false
        }
    }

    /**
     * 更新单个用户角色分配（状态/过期时间/角色）
     */
    const updateUserRole = async (
        id: number,
        data: Partial<{ role: number; expires_at: string | null; is_active: boolean; description: string }>
    ): Promise<UserRoleListItem> => {
        userRoleOperationLoading.value = true
        userRoleOperationError.value = null

        try {
            const response = await adminRolesApi.updateUserRole(id, data)

            // 同步本地列表项
            const index = userRoleList.value.findIndex(item => item.id === id)
            if (index !== -1) {
                userRoleList.value[index] = { ...userRoleList.value[index], ...response }
            }

            // 清缓存并刷新当前列表
            clearRoleCache()
            try {
                await loadUserRoleList(lastUserRoleQuery.value || {})
            } catch { }
            // 清缓存；本地合并即可，不再整表刷新，避免重复请求
            clearRoleCache()
            return response
        } catch (error: unknown) {
            if (error instanceof Error) {
                userRoleOperationError.value = error.message || '更新用户角色失败'
            } else {
                userRoleOperationError.value = '更新用户角色失败'
            }
            throw error
        } finally {
            userRoleOperationLoading.value = false
        }
    }

    /**
     * 给角色分配权限（仅超管）
     */
    const assignRolePermissions = async (roleId: number, data: RolePermissionAssignRequest): Promise<RolePermissionAssignResponse> => {
        permissionOperationLoading.value = true
        permissionOperationError.value = null

        try {
            const response = await adminRolesApi.assignPermissions(roleId, data)

            // 清除相关缓存
            clearRoleCache()

            return response
        } catch (error: unknown) {
            if (error instanceof Error) {
                permissionOperationError.value = error.message || '分配权限失败'
            } else {
                permissionOperationError.value = '分配权限失败'
            }
            throw error
        } finally {
            permissionOperationLoading.value = false
        }
    }

    /**
     * 撤销角色权限（仅超管）
     */
    const revokeRolePermissions = async (roleId: number, data: RolePermissionRevokeRequest): Promise<RolePermissionRevokeResponse> => {
        permissionOperationLoading.value = true
        permissionOperationError.value = null

        try {
            const response = await adminRolesApi.revokePermissions(roleId, data)

            // 清除相关缓存
            clearRoleCache()

            return response
        } catch (error: unknown) {
            if (error instanceof Error) {
                permissionOperationError.value = error.message || '撤销权限失败'
            } else {
                permissionOperationError.value = '撤销权限失败'
            }
            throw error
        } finally {
            permissionOperationLoading.value = false
        }
    }

    // ===== 搜索和筛选 =====

    /**
     * 搜索角色
     */
    const searchRoles = async (keyword: string) => {
        return loadRoleList({ search: keyword, page: 1 }, true)
    }

    /**
     * 按状态筛选角色
     */
    const filterRolesByStatus = async (isActive: boolean) => {
        return loadRoleList({ is_active: isActive, page: 1 }, true)
    }

    /**
     * 按类型筛选角色
     */
    const filterRolesByType = async (isSystem: boolean) => {
        return loadRoleList({ is_system: isSystem, page: 1 }, true)
    }

    // ===== 工具方法 =====

    /**
     * 清除角色相关缓存
     */
    const clearRoleCache = () => {
        cacheStore.removeCacheByPrefix('admin-roles-')
        cacheStore.removeCacheByPrefix('admin-role-detail-')
        cacheStore.removeCacheByPrefix('admin-role-users-')
        cacheStore.removeCacheByPrefix('admin-role-permissions-')
        // 同时清理用户角色列表缓存，避免分配/撤销后读取旧缓存
        cacheStore.removeCacheByPrefix('admin-user-roles-list-')
    }

    /**
     * 重置状态
     */
    const resetRoleState = () => {
        roleList.value = []
        currentRoleDetail.value = null
        currentRoleUsers.value = []
        currentRolePermissions.value = []
        userRoleList.value = []
        rolePagination.value = {
            total: 0,
            page: 1,
            size: 20,
            has_more: false
        }
        userRolePagination.value = {
            total: 0,
            page: 1,
            size: 20,
            has_more: false
        }
        lastRoleQuery.value = {}
        lastUserRoleQuery.value = {}

        // 重置错误状态
        roleListError.value = null
        roleDetailError.value = null
        roleOperationError.value = null
        permissionOperationError.value = null
        userRoleListError.value = null
        userRoleOperationError.value = null

        // 重置加载状态
        roleListLoading.value = false
        roleDetailLoading.value = false
        roleOperationLoading.value = false
        roleUsersLoading.value = false
        permissionOperationLoading.value = false
        userRoleListLoading.value = false
        userRoleOperationLoading.value = false
    }

    /**
     * 根据ID查找角色
     */
    const findRoleById = (id: number): Role | undefined => {
        return roleList.value.find(r => r.id === id)
    }

    /**
     * 根据名称查找角色
     */
    const findRoleByName = (name: string): Role | undefined => {
        return roleList.value.find(r => r.name === name)
    }

    return {
        // 状态
        roleList,
        currentRoleDetail,
        currentRoleUsers,
        currentRolePermissions,
        userRoleList,
        rolePagination,
        userRolePagination,
        lastRoleQuery,
        lastUserRoleQuery,

        // 加载状态
        roleListLoading,
        roleDetailLoading,
        roleOperationLoading,
        roleUsersLoading,
        permissionOperationLoading,
        userRoleListLoading,
        userRoleOperationLoading,

        // 错误状态
        roleListError,
        roleDetailError,
        roleOperationError,
        permissionOperationError,
        userRoleListError,
        userRoleOperationError,

        // 计算属性
        systemRoles,
        customRoles,
        activeRoles,
        inactiveRoles,
        roleStats,
        rolesByPermissionCount,
        rolesByUserCount,

        // 角色操作方法
        loadRoleList,
        getRoleUsers,
        getRolePermissions,
        createRole,
        updateRole,
        deleteRole,

        // 权限分配方法
        assignRolePermissions,
        revokeRolePermissions,

        // 用户角色管理方法
        loadUserRoleList,
        assignUserRoles,
        revokeUserRoles,

        // 搜索筛选方法
        searchRoles,
        filterRolesByStatus,
        filterRolesByType,

        // 工具方法
        clearRoleCache,
        resetRoleState,
        findRoleById,
        findRoleByName
        ,
        // 暴露：更新单个用户角色
        updateUserRole
    }
})